import React from "react";

import { Alert } from "~/components/alert";
import { For } from "~/components/for";
import { FormLabel } from "~/components/form/label";
import { IconDelete } from "~/components/icon";
import { useScope } from "~/tools/impulse";
import type { ImpulseFormList } from "~/tools/impulse-form";
import { max } from "~/tools/max";
import { sortBy } from "~/tools/sort-by";
import { splice } from "~/tools/splice";

import { WidgetColorField } from "../../../widget-form/color";
import { WidgetColor } from "../../../widget-form/color/form-state";
import { WidgetThresholdsModeField, WidgetThresholdsValueField } from "../../../widget-form/thresholds";
import { WidgetThresholdsMode } from "../../../widget-form/thresholds/form-state";
import { SettingsSectionFormField } from "../_field";
import { SettingsSectionGrid } from "../_section-grid";

import { StepFormState } from "./step-form-state";

function getPresetColor(index: number): string {
	return WidgetColor.preset[index % WidgetColor.preset.length];
}

export interface ThresholdsSectionProps {
	defaultColor?: WidgetColor;
	mode?: WidgetThresholdsMode;
	thresholds: ImpulseFormList<StepFormState>;
}

const DEFAULT_MODE = WidgetThresholdsMode.init();

export const ThresholdsSection: React.FC<ThresholdsSectionProps> = ({
	defaultColor,
	mode = DEFAULT_MODE,
	thresholds,
}) => {
	const scope = useScope();
	const steps = thresholds.getElements(scope);

	return (
		<div className="space-y-4">
			<SettingsSectionGrid className="grid-cols-[minmax(80px,auto)_1fr]">
				{steps.length > 0 && (
					<SettingsSectionFormField layout="horizontal" state={mode}>
						<FormLabel>Mode</FormLabel>
						<WidgetThresholdsModeField
							disabled={mode === DEFAULT_MODE}
							mode={mode}
							name="dashboard-widget-thresholds-mode"
						/>
					</SettingsSectionFormField>
				)}

				<For
					className="col-span-full grid grid-cols-subgrid gap-y-2"
					each={steps}
					empty={
						!defaultColor && <Alert className="col-span-full">No thresholds are configured for this widget yet.</Alert>
					}
				>
					{(threshold, index, arr) => (
						<SettingsSectionFormField key={index} layout="horizontal" state={threshold}>
							<FormLabel>Threshold {arr.length - index}</FormLabel>

							<div className="flex flex-row items-center gap-2">
								<SettingsSectionFormField state={threshold.fields.color}>
									<WidgetColorField color={threshold.fields.color} />
								</SettingsSectionFormField>

								<SettingsSectionFormField className="flex-1" layout="horizontal" state={threshold.fields.value}>
									{(fieldScope, field) => (
										<WidgetThresholdsValueField
											name={`dashboard-widget-threshold-${index}-value`}
											onBlur={() => {
												if (field.isDirty(fieldScope)) {
													thresholds.setElements((current, innerScope) =>
														sortBy(current, [({ fields }) => fields.value.getOutput(innerScope) ?? 0, "desc"]),
													);
												}
											}}
											value={field}
										/>
									)}
								</SettingsSectionFormField>

								<button
									className="button button-tertiary button-square"
									onClick={() => {
										thresholds.setElements(splice(index, 1, []));
									}}
									type="button"
								>
									<IconDelete className="size-3" />
								</button>
							</div>
						</SettingsSectionFormField>
					)}
				</For>

				{defaultColor && (
					<SettingsSectionFormField layout="horizontal" state={defaultColor}>
						<FormLabel>Default</FormLabel>
						<WidgetColorField color={defaultColor} />
					</SettingsSectionFormField>
				)}
			</SettingsSectionGrid>

			<button
				className="button button-secondary w-full uppercase"
				onClick={() => {
					thresholds.setElements((current, innerScope) => {
						const maxElementsValue = max(current.map((threshold) => threshold.fields.value.getOutput(innerScope))) ?? 0;

						return [
							StepFormState.init({
								color: getPresetColor(current.length),
								value: (maxElementsValue + 10).toString(),
							}),
							...current,
						];
					});
				}}
				type="button"
			>
				Add threshold
			</button>
		</div>
	);
};
